import { useState, type ReactNode } from 'react'
import {
  Download,
  FileText,
  FileType,
  Receipt,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Bolletta } from '@/lib/graphql/types'

type DownloadItem = {
  key: 'semplice' | 'dettaglio' | 'bollettino'
  label: string
  url: string
  icon: LucideIcon
}

/**
 * Sezione "Scarica bolletta": fino a 3 righe (Semplice / Dettaglio / Bollettino),
 * mostra solo quelle disponibili. Al click → dialog di conferma → apre il PDF
 * in nuova scheda (sul web non ha senso replicare il `WebView` mobile: il
 * browser scarica nativamente il PDF se `Content-Disposition: attachment`
 * o lo apre nel viewer integrato).
 */
export function DownloadSection({ invoice }: { invoice: Bolletta }) {
  const items: DownloadItem[] = [
    invoice.url_semplice && {
      key: 'semplice' as const,
      label: 'Bolletta Semplice',
      url: invoice.url_semplice,
      icon: FileText,
    },
    invoice.url_dettaglio && {
      key: 'dettaglio' as const,
      label: 'Bolletta Dettaglio',
      url: invoice.url_dettaglio,
      icon: FileType,
    },
    invoice.url_bollettino && {
      key: 'bollettino' as const,
      label: 'Bollettino',
      url: invoice.url_bollettino,
      icon: Receipt,
    },
  ].filter(Boolean) as DownloadItem[]

  const [pending, setPending] = useState<DownloadItem | null>(null)

  if (items.length === 0) return null

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base font-bold text-foreground">Scarica bolletta</h2>
      <Card>
        <ul>
          {items.map((it, idx) => (
            <li
              key={it.key}
              className={idx > 0 ? 'border-t border-border' : undefined}
            >
              <button
                type="button"
                onClick={() => setPending(it)}
                className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-accent/40 focus-visible:bg-accent/60 focus-visible:outline-none"
              >
                <it.icon className="h-5 w-5 text-secondary" />
                <span className="flex-1 text-sm text-foreground">{it.label}</span>
                <Download className="h-4 w-4 text-secondary" />
              </button>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
      >
        {pending && (
          <DialogContent>
            <DialogHeader>
              <DialogIcon>
                <pending.icon className="h-5 w-5" />
              </DialogIcon>
              <DialogTitle>Scarica {pending.label}</DialogTitle>
              <DialogDescription>
                Il PDF verrà aperto in una nuova scheda. Potrai salvarlo o stamparlo dal
                viewer del browser.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPending(null)}>
                Annulla
              </Button>
              <Button
                onClick={() => {
                  window.open(pending.url, '_blank', 'noopener,noreferrer')
                  setPending(null)
                }}
              >
                Apri PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </section>
  )
}

function DialogIcon({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-secondary/15 text-secondary">
      {children}
    </div>
  )
}
